import { Link } from "react-router-dom";
import { Mail, MapPin, Clock } from "lucide-react";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { Wordmark } from "./Logo";
import { CATEGORIES, SERVICES } from "@/data/services";
import { CONTACT, waHref } from "@/data/contact";
import { KEY_POINTS } from "@/data/testimonials";
import { usePortfolioCount } from "@/lib/portfolio";
import { LANDING } from "@/data/landing";
import { FooterExplorer } from "./FooterExplorer";
import { LOCATIONS } from "@/data/locations";
import { STATES } from "@/data/states";
import { Contours } from "./Vectors";

const COMPANY = [["About", "/about"], ["Services", "/services"], ["Pricing", "/pricing"], ["Outsourcing", "/outsourcing"], ["Insights", "/insights"], ["Locations", "/locations"], ["Service Areas (50 States)", "/service-areas"], ["Career", "/career"], ["Contact", "/contact"], ["Get Started", "/get-started"]];

export const Footer = () => {
  const portfolioCount = usePortfolioCount();
  const company = portfolioCount > 0 ? [...COMPANY.slice(0, 3), ["Portfolio", "/portfolio"], ...COMPANY.slice(3)] : COMPANY;
  return (
  <footer data-testid="site-footer" className="on-dark relative overflow-hidden bg-[#0A1D30] text-white">
    <Contours className="absolute -right-40 -top-20 w-[900px] text-[#1769AA] opacity-20" />
    <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 pt-20 pb-10">
      <div className="grid gap-12 lg:grid-cols-12 border-b border-white/10 pb-14">
        <div className="lg:col-span-8 grid gap-12 sm:grid-cols-2 lg:grid-cols-8 content-start">
        <div className="sm:col-span-2 lg:col-span-3">
          <Wordmark />
          <p className="mt-6 font-display text-2xl font-bold leading-snug max-w-sm">From Coordinates to <span className="text-[#D8B66A]">CAD Excellence.</span></p>
          <p className="mt-4 text-sm text-[#b8c7d6] max-w-sm leading-relaxed">Land surveying CAD drafting, survey data processing and digital survey mapping, prepared from client-supplied survey data.</p>
          <a href="mailto:survey@geodrafting.com" data-testid="footer-email-link" className="u-link mt-6 inline-flex items-center gap-2 text-sm"><Mail className="h-4 w-4" />survey@geodrafting.com</a>
          <ul className="mt-3 space-y-2" data-testid="footer-whatsapp-list">
            {CONTACT.whatsapp.map((n) => (
              <li key={n.region}>
                <a href={waHref(n)} target="_blank" rel="noreferrer" data-testid={`footer-whatsapp-${n.region.toLowerCase()}`} className="u-link inline-flex items-center gap-2 text-sm">
                  <WhatsAppIcon className="h-4 w-4 text-[#25D366]" /><span className="font-mono text-[10px] tracking-widest text-[#D8B66A]">{n.region}</span>{n.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-2">
          <p className="eyebrow text-[#D8B66A] mb-5">Company</p>
          <ul className="space-y-3 text-sm">
            {company.map(([l, to]) => <li key={to}><Link to={to} className="u-link text-[#d5dee8]" data-testid={`footer-${l.toLowerCase().replace(" ", "-")}-link`}>{l}</Link></li>)}
          </ul>
        </div>
        <div className="lg:col-span-3" data-testid="footer-contact-block">
          <p className="eyebrow text-[#D8B66A] mb-5">Locations & hours</p>
          <div className="space-y-5 text-[13px] leading-relaxed text-[#b8c7d6]">
            {CONTACT.locations.map((l) => (
              <address key={l.country} className="not-italic">
                <span className="mb-1 flex items-center gap-2 font-semibold text-white"><MapPin className="h-3.5 w-3.5 text-[#D8B66A]" />{l.country}</span>
                {l.lines.map((x) => <span key={x} className="block">{x}</span>)}
              </address>
            ))}
            <div>
              <span className="mb-1 flex items-center gap-2 font-semibold text-white"><Clock className="h-3.5 w-3.5 text-[#D8B66A]" />Business hours</span>
              {CONTACT.hours.map((h) => <span key={h.days} className="block">{h.days}: {h.time}</span>)}
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#9fb3c8]">{CONTACT.serving}</p>
          </div>
        </div>
        {/* key points — fills the space beside the tall directory column */}
        <div className="sm:col-span-2 lg:col-span-8 border-t border-white/10 pt-10" data-testid="footer-key-points">
          <p className="eyebrow text-[#D8B66A] mb-6">Why GEO DRAFTING</p>
          <ul className="grid gap-x-8 gap-y-6 sm:grid-cols-2 xl:grid-cols-3">
            {KEY_POINTS.map(([t, d], i) => (
              <li key={t} className="group flex gap-4" data-testid={`footer-key-point-${i + 1}`}>
                <span className="mt-0.5 shrink-0 font-mono text-[11px] text-[#D8B66A]">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <p className="text-[13px] font-semibold text-white transition-colors group-hover:text-[#5AA9E6]">{t}</p>
                  <p className="mt-1 text-xs leading-relaxed text-[#b8c7d6]">{d}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        </div>
        <div className="lg:col-span-4">
          <p className="eyebrow text-[#D8B66A] mb-5">Survey Drafting Directory</p>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
            {CATEGORIES.map((c) => (
              <div key={c.id}>
                <Link to={`/services?cat=${c.id}`} className="u-link text-[13px] font-semibold text-white" data-testid={`footer-cat-${c.id}`}>{c.code}. {c.label}</Link>
                <ul className="mt-2 space-y-1.5">
                  {SERVICES.filter((s) => s.cat === c.id).slice(0, 4).map((s) => (
                    <li key={s.slug}><Link to={`/services/${s.slug}`} className="u-link text-xs text-[#b8c7d6]">{s.title}</Link></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
      <FooterExplorer />
      {/* SEO links band: specialty landing pages, markets, top states (playbook §3.4) */}
      <nav aria-label="Specialty pages and markets" className="mt-10 grid gap-8 border-y border-white/10 py-8 text-[12px] md:grid-cols-3" data-testid="footer-seo-band">
        <div><p className="eyebrow mb-3 text-[10px] text-[#D8B66A]">Specialty</p><ul className="space-y-1.5">{LANDING.map((l) => <li key={l.slug}><Link to={`/${l.slug}`} className="u-link text-[#b8c7d6]" data-testid={`footer-landing-${l.slug}`}>{l.title}</Link></li>)}</ul></div>
        <div><p className="eyebrow mb-3 text-[10px] text-[#D8B66A]">Markets</p><ul className="space-y-1.5">{LOCATIONS.map((l) => <li key={l.slug}><Link to={["usa", "canada", "uk", "australia"].includes(l.slug) ? `/${l.slug}` : `/locations/${l.slug}`} className="u-link text-[#b8c7d6]">{l.title}</Link></li>)}<li><Link to="/service-areas" className="u-link text-[#b8c7d6]">Survey CAD drafting in all 50 US states</Link></li></ul></div>
        <div><p className="eyebrow mb-3 text-[10px] text-[#D8B66A]">Top US states</p><ul className="grid grid-cols-2 gap-x-4 gap-y-1.5">{["TX", "FL", "CA", "NY", "PA", "OH", "IL", "GA", "NC", "MI", "WA", "CO"].map((a) => { const s = STATES.find((x) => x.abbr === a); return <li key={a}><Link to={`/service-areas/${s.slug}`} className="u-link text-[#b8c7d6]">{s.name} survey drafting</Link></li>; })}</ul></div>
      </nav>
      <div className="pt-8 flex flex-col md:flex-row gap-4 md:items-center justify-between text-xs text-[#9fb3c8]">
        <p>© {new Date().getFullYear()} GEO DRAFTING. Drafting is performed from client-supplied survey data; field surveying and professional certification remain with the licensed surveyor.</p>
        <div className="flex gap-6 shrink-0">
          <Link to="/privacy-policy" className="u-link" data-testid="footer-privacy-link">Privacy Policy</Link>
          <Link to="/terms-and-conditions" className="u-link" data-testid="footer-terms-link">Terms & Conditions</Link>
        </div>
      </div>
    </div>
  </footer>
  );
};
